// src/encounter/ips-education-dialog.tsx
// Add/Edit dialog for the rail's Education card (PROD-1272). PRESENTATIONAL — composes DS Modal +
// Select (education level, from the master GET /api/Master/Education list) + TextArea (Education
// information). Mirrors Flutter education_form.dart / add_edit_education_widget.dart. No
// hooks/network: the host supplies `existing` (pre-fill), the level options, and the submit/close
// callbacks; on submit it emits an EducationFormValue which the rail turns into a wire body via
// buildEducationRequest + useSaveEducation.
import { useMemo, useState } from 'react';
import { Select, TextArea, HelperText, type SelectOption } from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import type { PatientEducation } from '@/types/patient-detail-api';
import type { EducationFormValue, EducationLevel } from '@/encounter/lib/to-education-request';

export interface IpsEducationDialogProps {
  open: boolean;
  /** Existing entity → Edit (pre-filled); null/empty → Add. */
  existing?: PatientEducation | null;
  /** Master education-level options (GET /api/Master/Education). */
  levelOptions: EducationLevel[];
  levelsLoading?: boolean;
  saving?: boolean;
  onSubmit: (value: EducationFormValue) => void;
  onClose: () => void;
}

const FIELD_GROUP = 'mb-4';

function isValidEducation(e: PatientEducation | null | undefined): boolean {
  return !!(e && (e.educationCode?.trim() || e.educationDisplay?.trim()));
}

/** Outer gate + keyed remount so each open re-initialises from `existing` via lazy useState. */
export function IpsEducationDialog(props: IpsEducationDialogProps) {
  if (!props.open) return null;
  return <IpsEducationDialogForm key={props.existing?.id ?? 'add'} {...props} />;
}

function IpsEducationDialogForm({
  existing = null,
  levelOptions,
  levelsLoading,
  saving,
  onSubmit,
  onClose,
}: IpsEducationDialogProps) {
  const isEditing = isValidEducation(existing);
  const existingCode = existing?.educationCode?.trim() ?? '';
  const existingDisplay = existing?.educationDisplay?.trim() ?? '';
  // The existing level's `id` so the edit pre-selects + round-trips even when its code isn't in the
  // master list — incl. a display-only entry (Flutter allows it via isValidData / keys the dropdown
  // by code, so a display-only or stale-code entry would otherwise be unselectable + unsaveable).
  const EXISTING_KEY = '__existing_level__';
  // Initial selection: the entity's code if present, else a sentinel for a display-only entry.
  const [levelCode, setLevelCode] = useState<string>(() => existingCode || (existingDisplay ? EXISTING_KEY : ''));
  const [info, setInfo] = useState<string>(() => existing?.educationInformation ?? '');
  const [error, setError] = useState<string | null>(null);

  const options = useMemo<SelectOption<string>[]>(() => {
    const master = (levelOptions ?? []).map((l) => ({ value: l.code, label: l.display || l.code }));
    // If editing an entity whose level isn't in the master list, prepend it so it pre-selects and
    // round-trips unchanged (a superset of Flutter's code-only matching — never a data-losing edit).
    const inMaster = existingCode && master.some((o) => o.value === existingCode);
    if ((existingCode || existingDisplay) && !inMaster) {
      return [{ value: existingCode || EXISTING_KEY, label: existingDisplay || existingCode }, ...master];
    }
    return master;
  }, [levelOptions, existingCode, existingDisplay]);

  function handleSubmit() {
    // Resolve the picked code → full level row. The stored level may not be in the freshly-loaded
    // list (or it hasn't loaded); BLOCK submit with a visible error rather than save an empty level
    // — a stale/empty dropdown must never silently clear education on an edit (mirrors the social
    // dialog's quantity guard).
    let level: EducationLevel | null = null;
    if (levelCode === EXISTING_KEY || (levelCode === existingCode && existingCode !== '')) {
      // The pre-selected existing level (display-only or stale-code) — round-trip it verbatim.
      const fromMaster = (levelOptions ?? []).find((l) => l.code === levelCode);
      level = fromMaster ?? { code: existingCode, display: existingDisplay, system: existing?.educationSystem?.trim() ?? '' };
    } else {
      level = (levelOptions ?? []).find((l) => l.code === levelCode) ?? null;
    }
    if (!level || (!level.code?.trim() && !level.display?.trim())) {
      setError('Pick an education level.');
      return;
    }
    onSubmit({ level, educationInformation: info });
  }

  return (
    <AddSurface
      open
      onClose={onClose}
      title={isEditing ? 'Edit education' : 'Add education'}
      size="md"
      commit={{
        run: handleSubmit,
        label: 'Save education',
        text: saving ? 'Saving…' : isEditing ? 'Save' : 'Add',
        busy: saving,
      }}
    >
      <div className="font-sans">
        <div className={FIELD_GROUP}>
          <Select
            label="Education level"
            options={options}
            value={levelCode || undefined}
            onChange={(v) => { setLevelCode(v); setError(null); }}
            disabled={saving || levelsLoading}
            placeholder={levelsLoading ? 'Loading…' : 'Pick an education level'}
          />
        </div>
        <div className={FIELD_GROUP}>
          <TextArea
            label="Education information"
            value={info}
            onChange={(e) => setInfo(e.target.value)}
            placeholder="Optional — e.g. field of study, year"
            rows={3}
            disabled={saving}
          />
        </div>
        {error ? <HelperText tone="error">{error}</HelperText> : null}
      </div>
    </AddSurface>
  );
}
